import React, { useEffect, useState, useRef, useCallback } from "react";
import "./Projects.css";
import {
  UilAngleDown,
  UilSearch,
  UilPlus,
  UilTimes,
} from "@iconscout/react-unicons";
import { useLocation, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { BeatLoader } from "react-spinners";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import { useTranslation } from "react-i18next";
import { resolveUrl } from "../../utils/storeUtils";
import Seo from "../../components/Seo/Seo";

/** Static keys for URL `categoryParams` → sidebar labels (see Search for translated labels). */
const PROJECT_URL_CATEGORIES = [
  { key: "All", label: "All", arKey: "الكل" },
  { key: "Work Space", label: "Work Space", arKey: "مساحة العمل" },
  { key: "Education", label: "Education", arKey: "تعليم" },
  { key: "Residential", label: "Residential", arKey: "سكني" },
  { key: "Entertainment", label: "Entertainment", arKey: "ترفيه" },
  { key: "Commercial", label: "Commercial", arKey: "تجاري" },
  { key: "Hospitality", label: "Hospitality", arKey: "ضيافة" },
];

const Projects = () => {
  const { categoryParams } = useParams();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const searchQueryFromURL = queryParams.get("search") || "";

  const [lang, setLang] = useState(localStorage.getItem("lang") || `"ar"`);
  const [searchQuery, setSearchQuery] = useState(searchQueryFromURL);
  const [dateRange, setDateRange] = useState({ fromDate: "", toDate: "" });
  const [selectedCategories, setSelectedCategories] = useState(
    categoryParams ? [categoryParams] : [lang === `"ar"` ? "الكل" : "All"]
  );

  useEffect(() => {
    if (categoryParams) {
      const updatedCategory =
        lang === `"ar"`
          ? PROJECT_URL_CATEGORIES.find((cat) => cat.key === categoryParams)?.arKey ||
            categoryParams
          : PROJECT_URL_CATEGORIES.find((cat) => cat.arKey === categoryParams)?.key ||
            categoryParams;
      setSelectedCategories([updatedCategory]);
    } else {
      setSelectedCategories([lang === `"ar"` ? "الكل" : "All"]);
    }
  }, [lang, categoryParams]);

  useEffect(() => {
    setSearchQuery(searchQueryFromURL);
  }, [searchQueryFromURL]);

  useEffect(() => {
    const handleStorageChange = () => {
      setLang(localStorage.getItem("lang") || `"ar"`);
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  return (
    <>
      <Seo
        title="مشاريعنا"
        description="استعرض مشاريع الرملاء للصناعات الخشبية المنفّذة في مساحات العمل والتعليم والسكن والضيافة."
        path="/projects"
      />
      <section className="projects projects--premium">
      <div className="container">
        <h1 className="projects-hero-title-sr">
          {lang === `"ar"` ? "مشاريعنا" : "Our Projects"}
        </h1>
        <Sidebar
          onDateChange={setDateRange}
          selectedCategories={selectedCategories}
          setSelectedCategories={setSelectedCategories}
          setDateRange={setDateRange}
        />
        <div className="boxs">
          <Search
            selectedCategories={selectedCategories}
            setSelectedCategories={setSelectedCategories}
            setSearchQuery={setSearchQuery}
            searchQuery={searchQuery}
          />
          <Content
            searchQuery={searchQuery}
            dateRange={dateRange}
            selectedCategories={selectedCategories}
          />
        </div>
      </div>
    </section>
    </>
  );
};

function Sidebar({
  onDateChange,
  selectedCategories,
  setSelectedCategories,
  setDateRange,
}) {
  const [openSidebar, setOpenSidebar] = useState(false);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [t] = useTranslation("global");
  const [lang, setLang] = useState(localStorage.getItem("lang") || `"ar"`);

  const handleDateChange = (dateType, value) => {
    if (dateType === "from") {
      setFromDate(value);
    } else {
      setToDate(value);
    }
    onDateChange({
      fromDate: dateType === "from" ? value : fromDate,
      toDate: dateType === "to" ? value : toDate,
    });
  };

  const handleToggleSidebar = () => {
    setOpenSidebar(!openSidebar);
  };

  const handleCheckboxChange = (label) => {
    const allLabel = lang === `"ar"` ? "الكل" : "All";

    if (label === allLabel) {
      setSelectedCategories([allLabel]);
    } else {
      const updatedCategories = selectedCategories.includes(allLabel)
        ? [label]
        : selectedCategories.includes(label)
        ? selectedCategories.filter((category) => category !== label)
        : [...selectedCategories, label];

      setSelectedCategories(updatedCategories);
    }
  };

  const handleToggleCategory = () => {
    document.querySelector(".cheackBoxs").classList.toggle("show");
    document.querySelector(".category .icon").classList.toggle("rotate");
  };

  const handleToggleTime = () => {
    document.querySelector(".timeBoxs").classList.toggle("show");
    document.querySelector(".time .icon").classList.toggle("rotate");
  };

  const handleClearAll = () => {
    const checkboxes = document.querySelectorAll(
      ".cheackBoxs input[type='checkbox']"
    );
    checkboxes.forEach((checkbox) => (checkbox.checked = false));
    setSelectedCategories([]);
    setDateRange({ fromDate: "", toDate: "" });
    setFromDate("");
    setToDate("");
  };

  useEffect(() => {
    const handleStorageChange = () => {
      setLang(localStorage.getItem("lang") || `"ar"`);
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  useEffect(() => {
    if (!openSidebar) return undefined;

    document.body.classList.add("projectsSidebarOpen");
    document.documentElement.classList.add("projectsSidebarOpen");
    const onKeyDown = (e) => {
      if (e.key === "Escape") setOpenSidebar(false);
    };
    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.classList.remove("projectsSidebarOpen");
      document.documentElement.classList.remove("projectsSidebarOpen");
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [openSidebar]);

  return (
    <>
      <div
        className={`sidebar-btn ${lang === `"ar"` ? "ar" : "en"} ${
          openSidebar ? "is-open" : ""
        }`}
        onClick={handleToggleSidebar}
        role="button"
        tabIndex={0}
        aria-label={t("sidebar.filters")}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleToggleSidebar();
          }
        }}
      >
        <UilPlus />
        <span className="sidebar-btn__label">{t("sidebar.filters")}</span>
      </div>
      {openSidebar ? (
        <div
          className="sidebar-overlay"
          onClick={handleToggleSidebar}
          aria-hidden="true"
        />
      ) : null}
      <div className={`sidebar ${openSidebar ? "" : "close"}`}>
        <div className="sidebarHeader">
          <div className="sidebarHeader__top">
            <button
              type="button"
              className="sidebar-close-btn"
              onClick={() => setOpenSidebar(false)}
              aria-label="Close filters"
            >
              <UilTimes size={20} />
            </button>
          </div>
          <div className="title">
            <div className="logo">
              <h3>{t("sidebar.filters")}</h3>
            </div>
            <p onClick={handleClearAll}>{t("sidebar.clear")}</p>
          </div>
        </div>
        <hr />
        <div className="category">
          <div className="title" onClick={handleToggleCategory}>
            <h3>{t("sidebar.category")}</h3>
            <div className="icon">
              <UilAngleDown size={35} color={"black"} />
            </div>
          </div>
          <div className="cheackBoxs">
            {[
              t("sidebar.workspace"),
              t("sidebar.education"),
              t("sidebar.residential"),
              t("sidebar.entertainment"),
              t("sidebar.commercial"),
              t("sidebar.hospitality"),
            ].map((label, index) => (
              <div className="cheackBox" key={index}>
                <label htmlFor={`check${index + 1}`}>{label}</label>
                <input
                  id={`check${index + 1}`}
                  type="checkbox"
                  checked={selectedCategories?.includes(label)}
                  onChange={() => handleCheckboxChange(label)}
                />
              </div>
            ))}
          </div>
        </div>
        <hr />
        <div
          className="time"
          style={{ opacity: "0", userSelect: "none", pointerEvents: "none" }}
        >
          <div className="title" onClick={handleToggleTime}>
            <h3>{t("sidebar.time")}</h3>
            <div className="icon">
              <UilAngleDown size={35} color={"black"} />
            </div>
          </div>
          <div className="forms timeBoxs">
            <div>
              <p>{t("sidebar.from")}</p>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => handleDateChange("from", e.target.value)}
              />
            </div>
            <div>
              <p>{t("sidebar.to")}</p>
              <input
                type="date"
                value={toDate}
                onChange={(e) => handleDateChange("to", e.target.value)}
              />
            </div>
          </div>
        </div>
        <hr />
      </div>
    </>
  );
}

function Content({ searchQuery, dateRange, selectedCategories }) {
  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [showFull, setShowFull] = useState(false);
  const [t] = useTranslation("global");
  const [lang, setLang] = useState(localStorage.getItem("lang") || `"ar"`);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [resetNonce, setResetNonce] = useState(0);
  const sentinelRef = useRef(null);
  const filterResetSkippedRef = useRef(true);
  const scrollLoadRef = useRef({
    isLoading: true,
    isLoadingMore: false,
    currentPage: 1,
    totalPages: 1,
  });

  const toggleShowFull = () => setShowFull(!showFull);

  useEffect(() => {
    const handleStorageChange = () => {
      setLang(localStorage.getItem("lang") || `"ar"`);
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  useEffect(() => {
    if (filterResetSkippedRef.current) {
      filterResetSkippedRef.current = false;
      return;
    }
    setProjects([]);
    setCurrentPage(1);
    setResetNonce((n) => n + 1);
  }, [searchQuery, dateRange, selectedCategories]);

  useEffect(() => {
    let cancelled = false;

    const fetchProjects = async () => {
      const append = currentPage > 1;
      try {
        if (append) setIsLoadingMore(true);
        else setIsLoading(true);

        const response = await axios.get(`/projects?page=${currentPage}`);
        if (cancelled) return;

        const batch = response?.data?.projects ?? [];
        const rawPages = response?.data?.totalPages;
        const pages =
          typeof rawPages === "number" && rawPages >= 0 ? rawPages : 1;

        setTotalPages(pages);
        setProjects((prev) => (currentPage === 1 ? batch : [...prev, ...batch]));
      } catch (error) {
        if (!cancelled) {
          const errorMessage =
            error.response?.data?.message || "Failed to fetch projects.";
          toast.error(errorMessage);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
          setIsLoadingMore(false);
        }
      }
    };

    fetchProjects();

    return () => {
      cancelled = true;
    };
  }, [currentPage, resetNonce]);

  useEffect(() => {
    scrollLoadRef.current = {
      isLoading,
      isLoadingMore,
      currentPage,
      totalPages,
    };
  }, [isLoading, isLoadingMore, currentPage, totalPages]);

  const tryLoadNextPage = useCallback(() => {
    const s = scrollLoadRef.current;
    if (s.isLoading || s.isLoadingMore || s.currentPage >= s.totalPages) return;
    setCurrentPage((p) => p + 1);
  }, []);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry?.isIntersecting) return;
        tryLoadNextPage();
      },
      { root: null, rootMargin: "320px 0px 320px 0px", threshold: 0 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [tryLoadNextPage, projects.length, totalPages]);

  const filteredProjects = projects.filter((project) => {
    const categoryKey = lang === `"ar"` ? project.arCategory : project.category;

    const isAllSelected = selectedCategories.includes(
      lang === `"ar"` ? "الكل" : "All"
    );

    const isCategoryMatch =
      isAllSelected || selectedCategories.includes(categoryKey);

    const isSearchMatch = (lang === `"ar"` ? project.arName : project.name)
      ?.toLowerCase()
      .includes(searchQuery.toLowerCase());

    const projectDate = new Date(project.createdAt);
    const from = dateRange.fromDate ? new Date(dateRange.fromDate) : null;
    const to = dateRange.toDate ? new Date(dateRange.toDate) : null;
    const isDateMatch =
      (!from || projectDate >= from) && (!to || projectDate <= to);

    return isCategoryMatch && isSearchMatch && isDateMatch;
  });

  const openModal = (project) => {
    setSelectedProject(project);
    setModalVisible(true);
  };

  const closeModal = () => {
    setSelectedProject(null);
    setModalVisible(false);
  };

  if (isLoading && projects.length === 0) {
    return (
      <div className="loader-container">
        <BeatLoader />
      </div>
    );
  }

  return (
    <>
      <div className="contentBoxs">
        {filteredProjects?.length === 0 && (
          <div className="message-box">
            <img src="/photos/file.svg" alt="No items" />
            <p>{t("projectsp.message")}</p>
          </div>
        )}
        <div className="projects">
          {filteredProjects?.length > 0 &&
            filteredProjects?.map((project, index) => (
              <div
                className="box"
                key={project?._id || project?.id || `project-${index}`}
                onClick={() => openModal(project)}
              >
                <img
                  src={resolveUrl(project?.image?.url, axios.defaults.baseURL)}
                  alt={project?.image?.alt}
                  loading="lazy"
                  decoding="async"
                />
                <div>
                  <p className="date">
                    {lang === `"ar"` ? project?.arName : project.name}
                  </p>
                </div>
              </div>
            ))}
        </div>
        {totalPages > 0 && currentPage < totalPages ? (
          <div
            ref={sentinelRef}
            className="projects-infinite-sentinel"
            aria-hidden="true"
          />
        ) : null}
        {isLoadingMore ? (
          <div className="projects-infinite-loading" aria-live="polite">
            <BeatLoader color="#9a7462" size={12} />
          </div>
        ) : null}

        {modalVisible && selectedProject && (
          <div className="modal-overlay" onClick={closeModal}>
            <div
              className="modal-content project"
              onClick={(e) => e.stopPropagation()}
            >
              <button className="close-btn" onClick={closeModal}>
                <i className="uil uil-times"></i>
              </button>
              <div className="content">
                <div className="image-container">
                  {(() => {
                    const mainUrl = selectedProject?.image?.url
                      ? String(selectedProject.image.url).trim()
                      : "";
                    const secondaries = Array.isArray(selectedProject?.secondaryImages)
                      ? selectedProject.secondaryImages
                      : Object.values(selectedProject?.secondaryImages || {});

                    const urls = [mainUrl, ...secondaries]
                      .map((u) => (u != null ? String(u).trim() : ""))
                      .filter(Boolean);

                    const seen = new Set();
                    const uniqueUrls = urls.filter((u) => {
                      const k = u.toLowerCase();
                      if (seen.has(k)) return false;
                      seen.add(k);
                      return true;
                    });

                    if (uniqueUrls.length === 0) return null;

                    return (
                      <Swiper
                        modules={[Navigation]}
                        navigation
                        spaceBetween={0}
                        slidesPerView={1}
                      >
                        {uniqueUrls.map((url, index) => (
                          <SwiperSlide key={`${index}-${url}`}>
                            <img
                              src={resolveUrl(url, axios.defaults.baseURL)}
                              alt={`Slide ${index + 1}`}
                              loading="lazy"
                              decoding="async"
                              className="project-modal-image"
                            />
                          </SwiperSlide>
                        ))}
                      </Swiper>
                    );
                  })()}
                </div>

                <h2>
                  {lang === `"ar"`
                    ? selectedProject?.arName
                    : selectedProject.name}
                </h2>

                <div className="content-item">
                  <span>{t("projectsp.category")}:</span>{" "}
                  <span>
                    {lang === `"ar"`
                      ? selectedProject?.arCategory
                      : selectedProject.category}
                  </span>
                </div>
                <hr />

                <div className="content-item description">
                  <span
                    style={{
                      width: "100%",
                      textAlign: lang === `"ar"` ? "right" : "left",
                    }}
                  >
                    {t("projectsp.description")}:
                  </span>{" "}
                  <span>
                    {showFull
                      ? lang === `"ar"`
                        ? selectedProject?.arDescription || "الوصف غير متاح"
                        : selectedProject?.description ||
                          "Description not available"
                      : lang === `"ar"`
                      ? selectedProject?.arDescription
                        ? `${selectedProject?.arDescription.slice(0, 100)}...`
                        : "الوصف غير متاح"
                      : selectedProject?.description
                      ? `${selectedProject?.description.slice(0, 100)}...`
                      : "Description not available"}
                  </span>
                  {(lang === `"ar"`
                    ? selectedProject?.arDescription?.length > 100
                    : selectedProject?.description?.length > 100) && (
                    <button onClick={toggleShowFull}>
                      {showFull
                        ? lang === `"ar"`
                          ? "عرض أقل"
                          : "Show Less"
                        : lang === `"ar"`
                        ? "عرض المزيد"
                        : "Show More"}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

function Search({
  selectedCategories,
  setSelectedCategories,
  setSearchQuery,
  searchQuery,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [t] = useTranslation("global");
  const [lang, setLang] = useState(localStorage.getItem("lang") || `"ar"`);
  const searchWrapRef = useRef(null);
  const searchInputRef = useRef(null);

  const categories = [
    { key: "All", label: t("projectsp.all"), arKey: "الكل" },
    {
      key: "Work Space",
      label: t("projectsp.workspace"),
      arKey: "مساحة العمل",
    },
    { key: "Education", label: t("projectsp.education"), arKey: "تعليم" },
    { key: "Residential", label: t("projectsp.residential"), arKey: "سكني" },
    {
      key: "Entertainment",
      label: t("projectsp.entertainment"),
      arKey: "ترفيه",
    },
    { key: "Commercial", label: t("projectsp.commercial"), arKey: "تجاري" },
    { key: "Hospitality", label: t("projectsp.hospitality"), arKey: "ضيافة" },
  ];

  useEffect(() => {
    const handleStorageChange = () => {
      setLang(localStorage.getItem("lang") || `"ar"`);
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  useEffect(() => {
    const handleOutside = (e) => {
      const el = searchWrapRef.current;
      if (!el) return;
      if (el.contains(e.target)) return;
      setIsOpen(false);
    };
    document.addEventListener("mousedown", handleOutside);
    document.addEventListener("touchstart", handleOutside, { passive: true });
    return () => {
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("touchstart", handleOutside);
    };
  }, []);

  const handleSelect = (key) => {
    if (key === "All") {
      setSelectedCategories([lang === `"ar"` ? "الكل" : "All"]);
    } else {
      const selectedKey =
        lang === `"ar"`
          ? categories.find((cat) => cat.key === key)?.arKey
          : key;
      setSelectedCategories([selectedKey]);
    }
    setIsOpen(false);
  };

  const selectedCategoryLabel =
    categories.find((category) =>
      lang === `"ar"`
        ? selectedCategories?.includes(category.arKey)
        : selectedCategories?.includes(category.key)
    )?.label || t("projectsp.all");

  return (
    <form
      ref={searchWrapRef}
      className="search"
      onMouseLeave={() => setIsOpen(false)}
      onSubmit={(e) => {
        e.preventDefault();
        setIsOpen(false);
        searchInputRef.current?.blur?.();
        document
          .querySelector(".projects .container .boxs .contentBoxs")
          ?.scrollIntoView?.({ behavior: "smooth", block: "start" });
      }}
    >
      <div className="searchField">
        <span className="searchField__icon" aria-hidden="true">
          <UilSearch size={18} />
        </span>
        <input
          className="search"
          ref={searchInputRef}
          placeholder={t("projectsp.search")}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      <div>
        <div
          className="select-box"
          onMouseEnter={() => setIsOpen(true)}
          onClick={() => setIsOpen((v) => !v)}
        >
          <div className="select-header" role="button" tabIndex={0}>
            {selectedCategoryLabel}
            <span
              className={`arrow ${isOpen ? "open" : ""}`}
              aria-hidden="true"
            />
          </div>
          {isOpen && (
            <div className="options">
              {categories.map((category) => (
                <div
                  key={category.key}
                  className="option"
                  onClick={() => handleSelect(category.key)}
                >
                  <label className="checkbox-container">
                    {lang === `"ar"` ? category.arKey : category.label}
                    <input
                      type="checkbox"
                      checked={
                        lang === `"ar"`
                          ? selectedCategories?.includes(category.arKey)
                          : selectedCategories?.includes(category.key)
                      }
                      readOnly
                    />
                  </label>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </form>
  );
}

export default Projects;
